import React, { useState } from 'react';
import { 
  Plus, Search, Edit2, Trash2, Package, Printer, Download, 
  QrCode, BarChart3, Settings, Eye, Copy, FileText, Tag,
  Upload, Image, Save, X
} from 'lucide-react';

const CargoLabelsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [filterType, setFilterType] = useState<string>('all');
  
  const [labelData, setLabelData] = useState({
    template_name: '',
    template_type: 'cargo' as 'cargo' | 'service' | 'inventory' | 'qr' | 'barcode',
    width_mm: 100,
    height_mm: 50,
    orientation: 'landscape' as 'portrait' | 'landscape',
    qr_code_enabled: false,
    barcode_enabled: false,
    logo_enabled: false,
    logo_file: null as File | null,
    background_color: '#ffffff',
    border_enabled: true,
    font_size: 12,
    company_name_enabled: true,
    address_enabled: true,
    phone_enabled: true,
    tracking_number_enabled: true
  });

  const templateTypes = [
    { value: 'cargo', label: 'Kargo Etiketi' },
    { value: 'service', label: 'Servis Etiketi' },
    { value: 'inventory', label: 'Envanter Etiketi' },
    { value: 'qr', label: 'QR Kod Etiketi' },
    { value: 'barcode', label: 'Barkod Etiketi' }
  ];

  const [mockTemplates, setMockTemplates] = useState([
    {
      id: 1,
      template_name: 'Standart Kargo Etiketi',
      template_type: 'cargo',
      width_mm: 100,
      height_mm: 50,
      qr_code_enabled: true,
      barcode_enabled: false,
      logo_enabled: true,
      is_default: true,
      created_at: '2024-01-01T00:00:00Z'
    },
    {
      id: 2,
      template_name: 'Servis Etiket Şablonu',
      template_type: 'service',
      width_mm: 80,
      height_mm: 40,
      qr_code_enabled: true,
      barcode_enabled: true,
      logo_enabled: false,
      is_default: false,
      created_at: '2024-01-02T00:00:00Z'
    },
    {
      id: 3,
      template_name: 'Envanter QR Etiketi',
      template_type: 'inventory',
      width_mm: 60,
      height_mm: 60,
      qr_code_enabled: true,
      barcode_enabled: false,
      logo_enabled: true,
      is_default: false,
      created_at: '2024-01-03T00:00:00Z'
    }
  ]);

  const filteredTemplates = mockTemplates.filter(template => {
    const matchesSearch = String(template.template_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || template.template_type === filterType;
    return matchesSearch && matchesType;
  });

  const handleEdit = (template: any) => {
    setEditingTemplate(template);
    setLabelData({
      template_name: template.template_name,
      template_type: template.template_type,
      width_mm: template.width_mm,
      height_mm: template.height_mm,
      orientation: template.width_mm > template.height_mm ? 'landscape' : 'portrait',
      qr_code_enabled: template.qr_code_enabled,
      barcode_enabled: template.barcode_enabled,
      logo_enabled: template.logo_enabled,
      logo_file: null,
      background_color: '#ffffff',
      border_enabled: true,
      font_size: 12,
      company_name_enabled: true,
      address_enabled: true,
      phone_enabled: true,
      tracking_number_enabled: true
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingTemplate(null);
    setLabelData({
      template_name: '',
      template_type: 'cargo',
      width_mm: 100,
      height_mm: 50,
      orientation: 'landscape',
      qr_code_enabled: false,
      barcode_enabled: false,
      logo_enabled: false,
      logo_file: null,
      background_color: '#ffffff',
      border_enabled: true,
      font_size: 12,
      company_name_enabled: true,
      address_enabled: true,
      phone_enabled: true,
      tracking_number_enabled: true
    });
    setShowModal(true);
  };

  const handleSave = () => {
    if (!labelData.template_name) {
      alert('Şablon adı gereklidir!');
      return;
    }

    if (editingTemplate) {
      // Update existing template
      const updatedTemplates = mockTemplates.map(template =>
        template.id === editingTemplate.id
          ? {
              ...template,
              template_name: labelData.template_name,
              template_type: labelData.template_type,
              width_mm: labelData.width_mm,
              height_mm: labelData.height_mm,
              qr_code_enabled: labelData.qr_code_enabled,
              barcode_enabled: labelData.barcode_enabled,
              logo_enabled: labelData.logo_enabled,
              updated_at: new Date().toISOString()
            }
          : template
      );
      setMockTemplates(updatedTemplates);
    } else {
      // Add new template
      const newTemplate = {
        id: Math.max(...mockTemplates.map(t => t.id)) + 1,
        template_name: labelData.template_name,
        template_type: labelData.template_type,
        width_mm: labelData.width_mm,
        height_mm: labelData.height_mm,
        qr_code_enabled: labelData.qr_code_enabled,
        barcode_enabled: labelData.barcode_enabled,
        logo_enabled: labelData.logo_enabled,
        is_default: false,
        created_at: new Date().toISOString()
      };
      setMockTemplates([...mockTemplates, newTemplate]);
    }

    setShowModal(false);
    alert(editingTemplate ? 'Şablon başarıyla güncellendi!' : 'Şablon başarıyla oluşturuldu!');
  };

  const handleDelete = (id: number) => {
    const templateToDelete = mockTemplates.find(t => t.id === id);
    if (!templateToDelete) return;

    if (confirm(`"${templateToDelete.template_name}" şablonunu silmek istediğinizden emin misiniz?`)) {
      const updatedTemplates = mockTemplates.filter(template => template.id !== id);
      setMockTemplates(updatedTemplates);
      alert('Şablon başarıyla silindi!');
    }
  };

  const handleCopy = (template: any) => {
    const copiedTemplate = {
      id: Math.max(...mockTemplates.map(t => t.id)) + 1,
      template_name: `${template.template_name} - Kopya`,
      template_type: template.template_type,
      width_mm: template.width_mm,
      height_mm: template.height_mm,
      qr_code_enabled: template.qr_code_enabled,
      barcode_enabled: template.barcode_enabled,
      logo_enabled: template.logo_enabled,
      is_default: false,
      created_at: new Date().toISOString()
    };
    setMockTemplates([...mockTemplates, copiedTemplate]);
    alert('Şablon başarıyla kopyalandı!');
  };

  const handlePreview = (template: any) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const handlePrint = (template: any) => {
    alert(`"${template.template_name}" şablonu yazdırılıyor...`);
    // Here would be the actual print logic
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setLabelData({...labelData, logo_file: file, logo_enabled: true});
      } else {
        alert('Lütfen geçerli bir resim dosyası seçin!');
      }
    }
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Kargo Etiketleri</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">Gönderilen ürünler için etiket şablonları</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Şablon
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Şablon ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 min-h-[44px]"
          />
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 min-h-[44px]"
        >
          <option value="all">Tüm Tipler</option>
          {templateTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div
            key={template.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Tag className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {template.template_name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                    {templateTypes.find(t => t.value === template.template_type)?.label}
                  </p>
                </div>
              </div>
              {template.is_default && (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  Varsayılan
                </span>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Boyut:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {template.width_mm}x{template.height_mm} mm
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">QR Kod:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {template.qr_code_enabled ? 'Evet' : 'Hayır'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Barkod:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {template.barcode_enabled ? 'Evet' : 'Hayır'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Logo:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {template.logo_enabled ? 'Evet' : 'Hayır'}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePreview(template)}
                    className="p-1 text-gray-400 hover:text-primary transition-colors"
                    title="Önizleme"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(template)}
                    className="p-1 text-gray-400 hover:text-primary transition-colors"
                    title="Düzenle"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleCopy(template)}
                    className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                    title="Kopyala"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Sil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => handlePrint(template)}
                  className="flex items-center px-3 py-1 bg-primary text-white rounded text-sm hover:bg-primary-dark transition-colors"
                >
                  <Printer className="w-3 h-3 mr-1" />
                  Yazdır
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Template Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingTemplate ? 'Şablon Düzenle' : 'Yeni Etiket Şablonu'}
              </h2>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Temel Bilgiler</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Şablon Adı *
                    </label>
                    <input
                      type="text"
                      value={labelData.template_name}
                      onChange={(e) => setLabelData({...labelData, template_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Kargo Etiket Şablonu"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Şablon Tipi *
                    </label>
                    <select
                      value={labelData.template_type}
                      onChange={(e) => setLabelData({...labelData, template_type: e.target.value as any})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      {templateTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Genişlik (mm) *
                    </label>
                    <input
                      type="number"
                      min="10"
                      value={labelData.width_mm}
                      onChange={(e) => setLabelData({...labelData, width_mm: parseInt(e.target.value) || 100})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Yükseklik (mm) *
                    </label>
                    <input
                      type="number"
                      min="10"
                      value={labelData.height_mm}
                      onChange={(e) => setLabelData({...labelData, height_mm: parseInt(e.target.value) || 50})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Yönlendirme
                    </label>
                    <select
                      value={labelData.orientation}
                      onChange={(e) => setLabelData({...labelData, orientation: e.target.value as 'portrait' | 'landscape'})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="landscape">Yatay</option>
                      <option value="portrait">Dikey</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Font Boyutu
                    </label>
                    <input
                      type="number"
                      min="8"
                      max="24"
                      value={labelData.font_size}
                      onChange={(e) => setLabelData({...labelData, font_size: parseInt(e.target.value) || 12})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Logo Upload */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Logo Ayarları</h3>
                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={labelData.logo_enabled}
                      onChange={(e) => setLabelData({...labelData, logo_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Logo Ekle</span>
                  </label>

                  {labelData.logo_enabled && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Logo Dosyası
                      </label>
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg cursor-pointer hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-600 transition-colors">
                          <Upload className="w-4 h-4 mr-2" />
                          Logo Seç
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                        </label>
                        {labelData.logo_file && (
                          <div className="flex items-center text-sm text-green-600">
                            <Image className="w-4 h-4 mr-1" />
                            {labelData.logo_file.name}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Features */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Etiket Özellikleri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={labelData.qr_code_enabled}
                      onChange={(e) => setLabelData({...labelData, qr_code_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">QR Kod</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={labelData.barcode_enabled}
                      onChange={(e) => setLabelData({...labelData, barcode_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Barkod</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={labelData.border_enabled}
                      onChange={(e) => setLabelData({...labelData, border_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Çerçeve</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={labelData.company_name_enabled}
                      onChange={(e) => setLabelData({...labelData, company_name_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Şirket Adı</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={labelData.address_enabled}
                      onChange={(e) => setLabelData({...labelData, address_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Adres</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={labelData.phone_enabled}
                      onChange={(e) => setLabelData({...labelData, phone_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Telefon</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={labelData.tracking_number_enabled}
                      onChange={(e) => setLabelData({...labelData, tracking_number_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Takip Numarası</span>
                  </label>
                </div>
              </div>

              {/* Background Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Arka Plan Rengi
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={labelData.background_color}
                    onChange={(e) => setLabelData({...labelData, background_color: e.target.value})}
                    className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={labelData.background_color}
                    onChange={(e) => setLabelData({...labelData, background_color: e.target.value})}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                <Save className="w-4 h-4 mr-2 inline" />
                {editingTemplate ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Etiket Önizleme
                </h2>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {selectedTemplate.template_name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedTemplate.width_mm}x{selectedTemplate.height_mm} mm
                </p>
              </div>

              {/* Mock Label Preview */}
              <div className="flex justify-center">
                <div 
                  className="border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 relative"
                  style={{
                    width: `${selectedTemplate.width_mm * 2}px`,
                    height: `${selectedTemplate.height_mm * 2}px`,
                    minWidth: '200px',
                    minHeight: '100px'
                  }}
                >
                  {selectedTemplate.logo_enabled && (
                    <div className="absolute top-2 left-2 w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                      <Image className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </div>
                  )}
                  
                  <div className="text-center">
                    <div className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">ÖRNEK ŞİRKET A.Ş.</div>
                    <div className="text-xs text-gray-700 dark:text-gray-300 mb-2">İstanbul, Türkiye</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">+90 212 555 01 01</div>
                    
                    {selectedTemplate.tracking_number_enabled && (
                      <div className="text-xs font-mono bg-gray-100 dark:bg-gray-700 p-1 rounded mb-2">
                        TK123456789TR
                      </div>
                    )}
                    
                    <div className="flex justify-center space-x-2">
                      {selectedTemplate.qr_code_enabled && (
                        <div className="w-8 h-8 bg-gray-900 flex items-center justify-center">
                          <QrCode className="w-6 h-6 text-white" />
                        </div>
                      )}
                      {selectedTemplate.barcode_enabled && (
                        <div className="w-12 h-6 bg-gray-900 flex items-center justify-center">
                          <BarChart3 className="w-8 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => handlePrint(selectedTemplate)}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <Printer className="w-4 h-4 mr-2 inline" />
                  Bu Şablonu Yazdır
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CargoLabelsPage;